import { Box, Center, Text, Tooltip } from '@mantine/core';
import { IconCheck, IconChecks, IconClock } from '@tabler/icons-react';

/**
 * Shared chat formatting helpers + tiny presentational atoms.
 *
 * These were originally file-local in the WhatsApp `chat.tsx`; they are
 * extracted here so the dedicated Instagram inbox (and, later, the unified
 * chat) render dates, timestamps and read receipts identically. The functions
 * are intentionally generic over the message shape so each channel can keep its
 * own row type.
 */

/** "Today" / "Yesterday" / "Mon, May 3". */
export function dayLabel(d: Date): string {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/** "14:05" — locale-aware hour:minute. */
export function formatTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Compact relative age: "now", "5m", "3h", or a date. */
export function timeAgo(d: Date): string {
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'now';
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  return d.toLocaleDateString();
}

export interface GroupableMessage {
  isIncoming: boolean;
  messagedAt?: string | Date | null;
}

export interface MessageBurst<T> {
  isIncoming: boolean;
  messages: T[];
}

export interface MessageDayGroup<T> {
  label: string;
  bursts: MessageBurst<T>[];
}

/**
 * Group messages by day, then by bursts of consecutive same-direction messages
 * within ~3 minutes — the standard chat layout. Input must be ascending by time.
 */
export function groupMessages<T extends GroupableMessage>(messages: T[]): MessageDayGroup<T>[] {
  const groups: MessageDayGroup<T>[] = [];
  let lastLabel = '';
  let lastBurst: MessageBurst<T> | null = null;
  let lastTime = 0;
  let currentGroup: MessageDayGroup<T> | null = null;

  for (const m of messages) {
    const when = m.messagedAt ? new Date(m.messagedAt) : new Date();
    const t = when.getTime();
    const label = dayLabel(when);
    if (label !== lastLabel || !currentGroup) {
      currentGroup = { label, bursts: [] };
      groups.push(currentGroup);
      lastLabel = label;
      lastBurst = null;
    }
    const sameBurst =
      lastBurst && lastBurst.isIncoming === m.isIncoming && t - lastTime < 3 * 60 * 1000;
    if (!sameBurst || !lastBurst) {
      lastBurst = { isIncoming: m.isIncoming, messages: [m] };
      currentGroup.bursts.push(lastBurst);
    } else {
      lastBurst.messages.push(m);
    }
    lastTime = t;
  }
  return groups;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export interface MessagingWindowState {
  kind: 'open' | 'human_agent' | 'closed';
  label: string;
  color: string;
}

/**
 * Client-side mirror of the server `resolveMessagingWindow` for Messenger/IG:
 * within 24h a reply is free-form; 24h–7d needs the HUMAN_AGENT tag; beyond 7d
 * (or with no inbound) the window is closed and the customer must message first.
 */
export function messagingWindowState(lastIncomingAt: Date | null): MessagingWindowState {
  if (!lastIncomingAt) return { kind: 'closed', label: 'No inbound yet', color: 'gray' };
  const age = Date.now() - lastIncomingAt.getTime();
  if (age < DAY_MS) return { kind: 'open', label: 'Open · 24h', color: 'green' };
  if (age < 7 * DAY_MS) return { kind: 'human_agent', label: 'Human-agent · 7d', color: 'yellow' };
  return { kind: 'closed', label: 'Window closed', color: 'red' };
}

export function DateDivider({ label }: { label: string }) {
  return (
    <Center my="sm">
      <Box
        style={{
          padding: '4px 12px',
          borderRadius: 999,
          background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
          border: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
          fontSize: 11,
          fontWeight: 500,
          color: 'var(--mantine-color-dimmed)',
        }}
      >
        {label}
      </Box>
    </Center>
  );
}

/**
 * Outbound delivery-state tick. Tuned for light message bubbles (read = blue
 * double-tick, delivered/sent = grey, failed = red cross, otherwise a clock).
 */
export function ReadReceipt({ status }: { status: string }) {
  if (status === 'failed') {
    return (
      <Tooltip label="Failed to send">
        <Text span size="xs" c="red.6">
          ✕
        </Text>
      </Tooltip>
    );
  }
  if (status === 'read') {
    return (
      <Tooltip label="Read">
        <IconChecks size={14} style={{ color: 'var(--mantine-color-blue-6)' }} />
      </Tooltip>
    );
  }
  if (status === 'delivered') {
    return (
      <Tooltip label="Delivered">
        <IconChecks size={14} style={{ color: 'var(--mantine-color-gray-6)' }} />
      </Tooltip>
    );
  }
  if (status === 'sent') {
    return (
      <Tooltip label="Sent">
        <IconCheck size={14} style={{ color: 'var(--mantine-color-gray-6)' }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip label="Sending">
      <IconClock size={12} style={{ color: 'var(--mantine-color-gray-5)' }} />
    </Tooltip>
  );
}
